import React from 'react'
import { Autocomplete } from '@equinor/eds-core-react'
import styled from 'styled-components'
import { PrimitiveAtom, useAtom } from 'jotai'
import { DeviceDto } from '@/core/models/types'
import { InputStyledLabel } from '@/styles/layout'
import ResponsiveFilterDialog from '@/components/responsiveFilterDialog/ResponsiveFilterDialog'
import { derivePortfolioFilterOptions } from '../utils/filterPortfolioDevices'
import {
    selectedFilterPortfolioAtexAtom,
    selectedFilterPortfolioIpRatingAtom,
    selectedFilterPortfolioMeasureTypeAtom,
    selectedFilterPortfolioProtocolAtom,
    selectedFilterPortfolioVendorAtom,
} from '@/core/store/globalStore'

type OptionKey = keyof ReturnType<typeof derivePortfolioFilterOptions>

interface Row {
    label: string
    key: OptionKey
    atom: PrimitiveAtom<string | null>
}

const ROWS: Row[] = [
    { label: 'Measure type', key: 'measureType', atom: selectedFilterPortfolioMeasureTypeAtom },
    { label: 'ATEX', key: 'atex', atom: selectedFilterPortfolioAtexAtom },
    { label: 'Protocol', key: 'protocol', atom: selectedFilterPortfolioProtocolAtom },
    { label: 'IP rating', key: 'ipRating', atom: selectedFilterPortfolioIpRatingAtom },
    { label: 'Vendor', key: 'vendor', atom: selectedFilterPortfolioVendorAtom },
]

const FilterRow: React.FC<{ row: Row; options: string[] }> = ({ row, options }) => {
    const [selected, setSelected] = useAtom(row.atom)
    return (
        <StyledAutocomplete
            label={<InputStyledLabel>{row.label}</InputStyledLabel>}
            options={options}
            optionLabel={(option: unknown) => option as string}
            selectedOptions={selected ? [selected] : []}
            onOptionsChange={(changes: { selectedItems: unknown[] }) =>
                setSelected(changes.selectedItems.length > 0 ? (changes.selectedItems[0] as string) : null)
            }
            onClear={() => setSelected(null)}
        />
    )
}

interface PortfolioFilterDialogProps {
    showMenu: boolean
    onClose: () => void
    devices: DeviceDto[]
    anchor: HTMLElement | null
}

const PortfolioFilterDialog: React.FC<PortfolioFilterDialogProps> = ({ showMenu, onClose, devices, anchor }) => {
    const options = derivePortfolioFilterOptions(devices)
    return (
        <ResponsiveFilterDialog open={showMenu} onClose={onClose} anchor={anchor} title="Filter">
            <Container>
                {ROWS.map((row) => (
                    <FilterRow key={row.key} row={row} options={options[row.key]} />
                ))}
            </Container>
        </ResponsiveFilterDialog>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    @media (max-width: 1024px) {
        margin-right: 16px;
        margin-left: 16px;
    }
`

const StyledAutocomplete = styled(Autocomplete).attrs({ autoWidth: true })`
    width: 100%;
`

export default PortfolioFilterDialog
